//#region [IMPORT]

// === THIRD-PARTY ===
import { useRef, useState } from 'react';

// ### MUI / STYLES ###
import { useTheme } from '@mui/material/styles';
import {
  Divider,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Settings,
} from '@mui/icons-material';

// === COMPONENTS ===
import Styled from 'components/Styled/Styled';
import Collapsible from 'components/Collapsible';
import { DragHandle  } from "components/DragDrop/DragHandle";
import { GroupDrag   } from 'components/DragDrop/GroupDrag';
import { ResizableGrid } from "../ResizableGrid/ResizableGrid";
import { useLayout } from "./PanelContext";
import { PanelDirection, PanelType } from "./PanelData";
import { DocumentCategoriesList } from "../DocumentCategoriesList/DocumentCategoriesList";
import { DocumentReview } from "../DocumentReview/DocumentReviews";
import { FileComments } from "../FileComments/FileComments";
import { ThumbPanel } from "../ThumbPanel/ThumbPanel";
import { ImagePanel } from "../ImagePanel/ImagePanel";

//#endregion

//-----------------------------------------------------------------
export function Panel( props ) {
  //-----------------------------------------------------------------
  //#region [VALUES]

  const { id } = props;
  const theme = useTheme();
  const {
    getParent,
    getItem, moveItem, openItem, closeItem,
    getSizes, setSizes,
    removeItem, createItem,
    changeDirection, setType
  } = useLayout();

  const data   = getItem  (id);
  const parent = getParent(id);

  const [settings_anchor, setSettingsAnchor] = useState(null);
  const is_open_settings = Boolean(settings_anchor);

  const [panel_type_anchor, setPanelTypeAnchor] = useState(null);
  const is_open_panel_type = Boolean(panel_type_anchor);

  //#endregion

  //#region [EVENTS]

  //-----------------------------------------------------------------
  function handleClick_Settings(event) {
    //-----------------------------------------------------------------
    event.stopPropagation();
    event.preventDefault();
    setSettingsAnchor(event.currentTarget)
  };

  //-----------------------------------------------------------------
  function handleClick_ChangeDir(event) {
    //-----------------------------------------------------------------
    closeSettings(event);

    changeDirection( id );
  }

  //-----------------------------------------------------------------
  function handleClick_Change(event) {
    //-----------------------------------------------------------------
    event.stopPropagation();
    event.preventDefault();
    setPanelTypeAnchor( event.currentTarget);
  }

  //-----------------------------------------------------------------
  function handleClick_Create(event) {
    //-----------------------------------------------------------------
    closeSettings(event);

    createItem( id );
  }

  //-----------------------------------------------------------------
  function handleClick_Remove(event) {
    //-----------------------------------------------------------------
    closeSettings(event);

    removeItem( id )
  }

  //-----------------------------------------------------------------
  function handleClick_PanelType(event, type) {
    //-----------------------------------------------------------------
    closePanelTypes(event);

    setType( id, type )
  }

  //-----------------------------------------------------------------
  function closeSettings(event) {
    //-----------------------------------------------------------------
    event.stopPropagation();
    event.preventDefault();
    setSettingsAnchor( null );
  }

  //-----------------------------------------------------------------
  function closePanelTypes(event) {
    //-----------------------------------------------------------------
    event.stopPropagation();
    event.preventDefault();
    setPanelTypeAnchor( null );
  }

  //#endregion

  //#region [RENDER]

  function display() {
    if( data == null ) return null;
    if( data.type === PanelType.ROOT ) {
      return displayChildren()
    }

    const drag_type = data.type === PanelType.ROOT ? 'root' : (data.type === PanelType.PANEL ? 'panel' : 'view');
    const header = getHeader();
    return (
      <GroupDrag data_type={drag_type} data={{id: data.id, index: props.index}} styles={[]} moveItem={moveItem} direction={parent.direction}>
        <Collapsible
          styles        = {[]}
          direction     = {parent.direction}
          is_open       = {data.is_open}
          handleOpen    = {()=> openItem(id)}
          handleClose   = {()=>closeItem(id)}
          header        = {header}
          header_closed = {header}
          body          = {{
            children: displayBody(),
            styles  : [{overflow:'auto'}]}}
          body_closed   = {{
            children: displayBody(),
            styles  : []
          }}
        />
      </GroupDrag>
    )
  }

  function getHeader() {
    if( data.type === PanelType.ROOT ) return null;
    const is_col = parent.direction === PanelDirection.COL;
    let name = '';
    let header_styles = {};
    switch( data.type ) {
      case PanelType.DOCUMENTS : name = 'Documents'; break;
      case PanelType.COMMENTS  : name = 'Comments'; break;
      case PanelType.REVIEW    : name = 'Fields'; break;
      case PanelType.THUMB     : name = 'Thumbnails'; break;
      case PanelType.IMAGE     : name = 'Image'; break;
      case PanelType.PANEL     : header_styles = { borderBottom: '2px solid '+theme.palette.primary.main}; break;
    }
    const header_settings = (
      <Styled styles={[{marginleft: 'auto'},{width:'fit-content'}]} onClick={()=>{}} >
        <Settings />
      </Styled>
    )
    const styles_inner = [{display:'flex'}];
    if( !data.is_open ) {
      if( is_col ) styles_inner.push( {height:'100%', width: '25px'} );
    }
    const header_body = (
      <Styled styles={styles_inner}>
        <DragHandle styles={[!data.is_open && is_col?{height:'60px'}:{}]} />
        <Styled styles={[ {fontWeight: 'bold'} ]}>
          {name}
        </Styled>
        {displayPanelSettings()}
      </Styled>
    )
    const styles_outer = [{ background: theme.palette.background.secondary[theme.palette.mode] },header_styles];
    if( !data.is_open ) {
      if( is_col ) {
        styles_outer.push( { height:'100%', width: '25px' } );
        styles_outer.push( { alignItems:'center', justifyContent: 'center', writingMode: 'vertical-rl', textOrientation:'upright'} );
      }
    }

    const header = {
      children: header_body,
      styles  : styles_outer
    };

    return header;
  }

  function displayBody() {
    if( data.type === PanelType.PANEL || data.type === PanelType.ROOT ) {
      return displayChildren();
    }

    let body = null;
    switch( data.type ) {
      case PanelType.DOCUMENTS : body = <DocumentCategoriesList />; break;
      case PanelType.COMMENTS  : body = <FileComments />; break;
      case PanelType.REVIEW    : body = <DocumentReview />; break;
      case PanelType.THUMB     : body = <ThumbPanel />; break;
      case PanelType.IMAGE     : body = <ImagePanel />; break;
    }

    return body;
  }

  function displayChildren() {
    return (
      <ResizableGrid
        style={{ height: "100%", width: "100%" }}
        columns={data.col_count}
        rows={data.row_count}
        sizes={getSizes(id)}
        resizable="both"
        dividerSize={8}
        dividerColor={theme.palette.primary.main}
        dividerTrim={theme.palette.background.primary[theme.palette.mode]}
        onChange={(sizes)=>{ setSizes(id,sizes) }}
        renderCell={(r, c, index) => {
          return <Panel id={data.items[index]} index={index} />
        }}
      />
    )
  }

  //-----------------------------------------------------------------
  function displayPanelSettings() {
    //-----------------------------------------------------------------
    return (
      <Styled styles={[{marginleft: 'auto'},{width:'fit-content'}]}>
        <Styled onClick={handleClick_Settings} >
          <Settings  />
        </Styled>
        <Menu
          id="basic-menu"
          anchorEl={settings_anchor}
          open={is_open_settings}
          onClose={closeSettings}
          slotProps={{
            list: {
              'aria-labelledby': 'basic-button',
            },
          }}
        >
          {data.type !== PanelType.PANEL ? null : <MenuItem onClick={handleClick_ChangeDir}>Change Direction</MenuItem>}
          {data.is_locked ? null : <Divider />}
          {data.is_locked ? null : <MenuItem onClick={handleClick_Change}>Change Panel</MenuItem>}
          {data.type !== PanelType.PANEL ? null : <Divider />}
          {data.type !== PanelType.PANEL ? null : <MenuItem onClick={handleClick_Create}>Add Panel</MenuItem>}
          {data.is_locked ? null : <Divider />}
          {data.is_locked ? null : <MenuItem onClick={handleClick_Remove}>Remove Panel</MenuItem>}
        </Menu>
        <Menu
          id="menu-panel-types"
          anchorEl={panel_type_anchor}
          open={is_open_panel_type}
          onClose={closePanelTypes}
          slotProps={{
            list: {
              'aria-labelledby': 'basic-button',
            },
          }}
        >
          {<MenuItem onClick={(event)=>handleClick_PanelType( event, PanelType.PANEL    )}>Panel     </MenuItem>}
          {<MenuItem onClick={(event)=>handleClick_PanelType( event, PanelType.DOCUMENTS)}>Documents </MenuItem>}
          {<MenuItem onClick={(event)=>handleClick_PanelType( event, PanelType.REVIEW   )}>Fields    </MenuItem>}
          {<MenuItem onClick={(event)=>handleClick_PanelType( event, PanelType.IMAGE    )}>Image     </MenuItem>}
          {<MenuItem onClick={(event)=>handleClick_PanelType( event, PanelType.THUMB    )}>Thumbnails</MenuItem>}
          {<MenuItem onClick={(event)=>handleClick_PanelType( event, PanelType.COMMENTS )}>Comments  </MenuItem>}
        </Menu>
      </Styled>
    )
  }

  //#endregion

  return display();
}


function SubMenuItem() {

}